<template>
    <div>
        <common-modal v-if="isOpen" :showAsInnerModal="showAsInnerModal" height="small" @close="$emit('close')">
            <common-content-spacer height="medium">
                <common-loader :isProcessing="isFetching || isLoading"></common-loader>
                <div v-if="latestReferral && !isFetching">
                    <!-- Header -->
                    <div class="referral-tracking-modal__header">
                        <h2>{{ $t('pages.referrals.index.referral_modal.head') }}</h2>
                    </div>

                    <!-- Patient Info -->
                    <div class="referral-tracking-modal__section">
                        <p class="section-title">{{ $t('pages.eprescribe.eprescribe.steps.step_3') }}</p>
                        <div class="card-refferal">
                            <div class="card-item">
                                <div class="field">
                                    <span class="label">{{ $t('forms.patient_profile.fields.pharmacy.name.lable') }}</span>
                                    <p class="value">
                                        <common-patient-name
                                            :patient="latestReferral?.patient"
                                            :clickable="latestReferral?.shouldShowPatientInfo !== false"
                                        ></common-patient-name>
                                    </p>
                                </div>
                                <div class="field">
                                    <span class="label">{{ $t('forms.patient.patient_information.fields.date.label') }}</span>
                                    <p class="value">{{ getPatientDateOfBirth(latestReferral?.patient?.dateOfBirth) }}</p>
                                </div>
                            </div>

                            <div class="card-item">
                                <div class="field">
                                    <span class="label">{{ $t('forms.referrals.make.fields.diagnosis.label') }}</span>
                                    <p class="referral-tracking__referrals blue">{{ latestReferral?.diagnosis }}</p>
                                </div>
                                <div class="field">
                                    <span class="label">{{ $t('forms.practice_ancillary.fields.insurance') }}</span>
                                    <p class="value">{{ latestReferral?.insurancePlan?.name }}</p>
                                </div>
                            </div>
                            <div
                                v-if="latestReferral?.telehealth !== undefined && latestReferral?.telehealth !== null"
                                class="card-item"
                            >
                                <div class="field">
                                    <span class="label">{{ $t('forms.referrals.search.fields.telehealth.label') }}</span>
                                    <p class="value">{{ getTelehealthPreference(latestReferral?.telehealth) }}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Provider -->
                    <div v-if="showProviderSection() && providerSectionType" class="referral-tracking-modal__section">
                        <p class="section-title">
                            {{ sectionTitle }}
                        </p>

                        <div class="card-provider">
                            <div class="provider-details">
                                <div class="provider">
                                    <div class="user-avatar">
                                        <common-user-avatar size="" :user="providerUser"></common-user-avatar>
                                    </div>

                                    <div>
                                        <p class="name">{{ getUserName(latestReferral) }}</p>
                                        <p class="sub">{{ getUserSpeciality(specialityUser, latestReferral) }}</p>
                                    </div>
                                </div>
                            </div>

                            <div v-if="latestReferral?.timeframe" class="field">
                                <p class="label">
                                    {{ $t('pages.auth.verify_token.referral_info.timeframe') }}
                                </p>
                                <span class="value">
                                    <p class="referral-tracking__referrals timeframe">{{ getTimeframeLabel(latestReferral) }}</p>
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Status -->
                    <div class="referral-tracking-modal__section">
                        <p class="section-title">{{ $t('modals.patient.medical.head') }}</p>
                        <div class="card-refferal">
                            <div class="card-item">
                                <div class="field">
                                    <p class="label">{{ $t('pages.referrals.index.referrals_made.stage') }}</p>
                                    <span :class="getStageClass(latestReferral)">
                                        {{ formatStage(latestReferral) }}
                                    </span>
                                </div>

                                <div class="field">
                                    <p class="label">{{ $t('pages.referrals.index.referrals_made.typer') }}</p>
                                    <span class="referral-tracking-modal__referrals" :class="referralTypeClass(sourceType)">
                                        {{ getReferralType(sourceType) }}
                                    </span>
                                </div>
                            </div>

                            <div class="card-item">
                                <div class="field">
                                    <p class="label">{{ $t('pages.referrals.index.referral_modal.date') }}</p>
                                    <p class="value">{{ getCreatedDate(latestReferral) }}</p>
                                </div>

                                <div v-if="latestReferral?.appointmentDateTime" class="field">
                                    <p class="label">{{ $t('pages.referrals.index.referral_modal.visit') }}</p>
                                    <p class="value">{{ scheduledForDate }}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <hr />

                    <!-- Progress -->
                    <div class="referral-tracking-modal__section">
                        <p class="section-title">{{ $t('pages.referrals.index.referral_modal.timeline') }}</p>

                        <div class="referral-tracking-modal__timeline">
                            <!-- step 1 -->
                            <div class="item done">
                                <div class="dot">
                                    <span class="check">✓</span>
                                </div>
                                <div class="referral-tracking-modal__timeline-referral">
                                    <p>{{ getStep1Label() }}</p>
                                    <span>{{ createdAt }}</span>
                                </div>
                            </div>
                            <div v-if="proposeSlotsCondition">
                                <div :class="['item', referralStatusClass]">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="latestReferral.status === ReferralStatusEnum.SCHEDULED" class="check">
                                                ✓
                                            </span>
                                            <span v-else-if="latestReferral.status === ReferralStatusEnum.INACTIVE" class="cross">
                                                X
                                            </span>
                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p v-if="latestReferral.status === ReferralStatusEnum.CREATED">
                                                {{ $t('pages.referrals.pool.accept_and_propose_time') }}
                                            </p>
                                            <common-buttons
                                                v-if="latestReferral.status === ReferralStatusEnum.CREATED"
                                                align="left"
                                                mobileDirection="normal"
                                            >
                                                <common-button
                                                    :label="$t('pages.referrals.notes.add.buttons.yes')"
                                                    type="primary"
                                                    size="tiny"
                                                    @click="processReferral()"
                                                ></common-button>
                                                <common-button
                                                    :label="$t('pages.referrals.show.buttons.decline')"
                                                    type="secondary"
                                                    size="tiny"
                                                    @click="declineReferral()"
                                                ></common-button>
                                                <common-button
                                                    :label="
                                                        $t(
                                                            'pages.referrals.new.dropdown.label.referred.request_additional_records',
                                                        )
                                                    "
                                                    type="track-referral"
                                                    size="tiny"
                                                    @click="openRequestAdditionalRecords"
                                                ></common-button>
                                            </common-buttons>
                                            <div v-if="showRecords" class="referral-tracking-modal__request-records">
                                                <p class="referral-tracking-modal__request-records-title">
                                                    {{ $t('modals.patient_profile.profile.request.record') }}
                                                </p>
                                                <common-loader
                                                    v-if="isFetchingDocuments"
                                                    :isProcessing="isFetchingDocuments"
                                                ></common-loader>

                                                <div v-else class="record-list">
                                                    <label
                                                        v-for="document in documentOptions"
                                                        :key="document.value"
                                                        class="record-item"
                                                    >
                                                        <input
                                                            v-model="selectedDocumentIds"
                                                            type="checkbox"
                                                            :value="document.value"
                                                        />

                                                        <span>{{ document.label }}</span>
                                                    </label>
                                                </div>
                                                <div class="requirement-notes">
                                                    <label class="requirement-notes__title">
                                                        {{ $t('forms.patient_profile.fields.profile.additional.label') }}
                                                    </label>

                                                    <textarea
                                                        v-model.trim="message"
                                                        class="requirement-notes__textarea"
                                                        :placeholder="
                                                            $t('forms.patient_profile.fields.profile.additional.placeholder')
                                                        "
                                                        rows="4"
                                                    ></textarea>
                                                </div>
                                                <common-buttons align="right">
                                                    <common-button
                                                        :isProcessing="isSendingRecordRequest"
                                                        :label="$t('pages.patient.details.button.primary')"
                                                        type="primary"
                                                        size="tiny"
                                                        @click="requestAdditionalRecords()"
                                                    ></common-button>
                                                </common-buttons>
                                            </div>
                                            <p v-else-if="latestReferral.status === ReferralStatusEnum.SCHEDULED">
                                                {{ $t('pages.referrals.tracking.status_description.CONFIRMED') }}
                                            </p>
                                            <p v-else-if="latestReferral.status === ReferralStatusEnum.INACTIVE">
                                                {{ $t('pages.referrals.tracking.status_description.INACTIVE_NEW') }}
                                            </p>
                                        </div>
                                    </div>
                                    <div class="right-content">
                                        <div v-if="latestReferral.status === ReferralStatusEnum.SCHEDULED">
                                            <a
                                                @click="
                                                    emitEvent({
                                                        name: 'open-direct-referral',
                                                        payload: latestReferral.referralId,
                                                    })
                                                "
                                            >
                                                {{ $t('pages.referrals.show.view_details.buttons.view_details') }}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="acceptSlotsConditon">
                                <div :class="['item', referralStatusClass]">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="latestReferral.status === ReferralStatusEnum.SCHEDULED" class="check">
                                                ✓
                                            </span>
                                            <span v-else-if="latestReferral.status === ReferralStatusEnum.INACTIVE" class="cross">
                                                X
                                            </span>
                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p v-if="latestReferral.status === ReferralStatusEnum.TIME_SLOTS_PROPOSED">
                                                {{ $t('pages.referrals.pool.choose_provider_slots') }}
                                            </p>
                                            <p v-else-if="latestReferral.status === ReferralStatusEnum.SCHEDULED">
                                                {{ $t('pages.referrals.tracking.status_description.CONFIRMED') }}
                                            </p>
                                            <p v-else-if="latestReferral.status === ReferralStatusEnum.INACTIVE">
                                                {{ $t('pages.referrals.tracking.status_description.INACTIVE_NEW') }}
                                            </p>
                                        </div>
                                    </div>
                                    <div class="right-content">
                                        <common-buttons v-if="latestReferral.status === ReferralStatusEnum.TIME_SLOTS_PROPOSED">
                                            <router-link :to="{ path: `/referrals/pool/${type}/${latestReferral.id}` }">
                                                <common-button
                                                    size="tiny"
                                                    :label="$t('common.microcopy.continue')"
                                                    type="primary"
                                                ></common-button>
                                            </router-link>
                                        </common-buttons>
                                        <div v-else-if="latestReferral.status === ReferralStatusEnum.SCHEDULED">
                                            <a
                                                @click="
                                                    emitEvent({
                                                        name: 'open-direct-referral',
                                                        payload: latestReferral.referralId,
                                                    })
                                                "
                                            >
                                                {{ $t('pages.referrals.show.view_details.buttons.view_details') }}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="uploadRecordsCondition">
                                <div :class="['item', uploadRecordsStatus]">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="isUploadRecordsCompleted" class="check">✓</span>
                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p>{{ uploadRecordsTitle }}</p>
                                            <common-buttons v-if="!isUploadRecordsCompleted" align="left">
                                                <common-button
                                                    size="tiny"
                                                    type="primary"
                                                    :label="$t('common.microcopy.continue')"
                                                    @click="continueUploadRecords"
                                                ></common-button>
                                            </common-buttons>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="showTimelineCondition">
                                <!-- step 2 -->
                                <div :class="step2Class">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="isApproved" class="check">✓</span>
                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p v-if="isApproved">
                                                {{ $t('pages.referrals.tracking.status_description.CONFIRMED') }}
                                            </p>
                                            <p v-else>{{ notApprovedText }}</p>

                                            <template v-if="isApproved">
                                                <span>{{ approvedAt }}</span>
                                            </template>
                                            <p
                                                v-if="scheduledForDate && scheduledForTime"
                                                class="referral-tracking-modal__scheduled-info"
                                            >
                                                {{ $t('pages.referrals.index.headings.scheduled_for') }}:
                                                <b>{{ scheduledForDate }}</b>
                                                {{ $t('pages.referrals.show.view_details.at') }}
                                                <b>{{ scheduledForTime }}</b>
                                            </p>
                                        </div>
                                    </div>
                                    <div v-if="shouldShowRightContent" class="right-content">
                                        <a @click="openReferralInfoModal()">
                                            {{ $t('pages.referrals.show.view_details.buttons.view_details') }}
                                        </a>
                                    </div>
                                </div>
                                <!-- step 3 -->
                                <div :class="['item', insuranceStatus]">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="insuranceStatus === 'done'" class="check">✓</span>

                                            <span v-else-if="insuranceStatus === 'declined'" class="cross">X</span>

                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p>{{ insuranceTimelineText }}</p>

                                            <template v-if="insuranceStatus === 'initial'">
                                                <p class="referral-tracking-modal__scheduled-info">
                                                    {{ latestReferral.insurancePlan.name }}
                                                </p>
                                            </template>

                                            <template v-else-if="insuranceStatus === 'done'">
                                                <span>{{ insuranceAuthObtainedAt }}</span>
                                                <p class="referral-tracking-modal__scheduled-info">
                                                    {{ latestReferral.insurancePlan.name }}
                                                    <b v-if="latestReferral.insuranceAuthNumber">
                                                        #{{ latestReferral.insuranceAuthNumber }}
                                                    </b>
                                                </p>
                                            </template>

                                            <template v-else>
                                                <span>
                                                    {{ $t('pages.referrals.show.details.insurance_plan') }}:
                                                    {{ latestReferral.insurancePlan.name }}
                                                </span>
                                            </template>
                                        </div>
                                    </div>

                                    <div v-if="showInsuranceUpdateButton" class="right-content">
                                        <common-button
                                            type="primary"
                                            size="tiny"
                                            :label="$t('pages.referrals.show.buttons.update_status')"
                                            @click="openInsuranceAuthorizationModal"
                                        ></common-button>
                                    </div>
                                </div>
                                <!-- step 4 -->
                                <div :class="step4Class">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="patientSeenStatus === 'done'" class="check">✓</span>

                                            <span v-else-if="patientSeenStatus === 'error'" class="cross">X</span>

                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p>{{ patientSeenTitle }}</p>

                                            <span>{{ step4Text }}</span>

                                            <p v-if="showPatientSeenPending" class="referral-tracking-modal__scheduled-info">
                                                {{ $t('pages.referrals.show.details.at') }}
                                                <b>{{ scheduledForTime }}</b>
                                            </p>
                                        </div>
                                    </div>

                                    <div class="right-content">
                                        <template v-if="isPatientRescheduleRequested && !isReferringPhysician">
                                            <common-button
                                                type="primary"
                                                size="tiny"
                                                :label="$t('pages.referrals.show.buttons.propose_time_slots')"
                                                @click="processReferral()"
                                            ></common-button>
                                        </template>
                                        <template v-else-if="showUpdateStatus">
                                            <common-button
                                                type="primary"
                                                size="tiny"
                                                :label="$t('pages.referrals.show.buttons.update_status')"
                                                @click="openPatientSeenModal"
                                            ></common-button>
                                        </template>
                                    </div>
                                </div>
                                <!-- step 5 -->
                                <div :class="step5Class">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="referralStatus === 'done'" class="check">✓</span>

                                            <span
                                                v-else-if="referralStatus === 'pending'"
                                                class="referral-tracking-modal__checks"
                                            ></span>

                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>
                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p>
                                                {{ referralStatusTitle }}
                                            </p>
                                            <common-buttons v-if="isConsultationNotesPendingReceived" align="left">
                                                <common-button
                                                    :label="$t('pages.referrals.notes.add.buttons.yes')"
                                                    type="primary"
                                                    size="tiny"
                                                    @click="reportAlreadyAdded"
                                                ></common-button>
                                                <common-button
                                                    :label="$t('pages.referrals.notes.add.buttons.no')"
                                                    type="secondary"
                                                    size="tiny"
                                                    @click="addNotesClicked"
                                                ></common-button>
                                            </common-buttons>
                                            <span v-if="latestReferral.status === ReferralStatusEnum.COMPLETED">
                                                {{ notesUploadedDate }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="right-content">
                                        <template v-if="isStatusCompletedAndUserNotOther">
                                            <router-link :to="referralRoute">
                                                <common-button
                                                    :label="$t('pages.referrals.show.view_details.view_report')"
                                                    type="primary"
                                                    size="tiny"
                                                ></common-button>
                                            </router-link>
                                        </template>
                                        <template v-else-if="isConsultationNotesPendingMade">
                                            <template v-if="!latestReferral.notesRequestDateTime && !reportNewRequestDateTime">
                                                <common-button
                                                    :label="$t('pages.referrals.show.view_details.buttons.request_report')"
                                                    type="primary"
                                                    size="tiny"
                                                    @click="openRequestReportModal"
                                                ></common-button>
                                            </template>

                                            <template v-else>
                                                <p>{{ $t('pages.referrals.show.details.report_requested') }}</p>
                                                <p>{{ referralReportRequestedDateTime }}</p>
                                                <a @click="openRequestReportModal">
                                                    {{ $t('pages.referrals.show.details.solicit_again') }}
                                                </a>
                                            </template>
                                        </template>
                                    </div>
                                </div>
                                <!-- step 6 -->
                                <div v-if="type == ReferralPathTypeEnum.RECEIVED" :class="step6Class">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="peerRatingStatus === 'done'" class="check">✓</span>
                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>
                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p>
                                                {{
                                                    isReferralCompleted(latestReferral) && latestReferral.physicianReview?.ratings
                                                        ? 'Rating received'
                                                        : $t('pages.referrals.show.view_details.rating_not_available')
                                                }}
                                            </p>
                                            <span
                                                v-if="
                                                    isReferralCompleted(latestReferral) && latestReferral.physicianReview?.ratings
                                                "
                                            >
                                                {{ peerRatingDate }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="right-content">
                                        <template
                                            v-if="isReferralCompleted(latestReferral) && latestReferral.physicianReview?.ratings"
                                        >
                                            <common-rating-stars
                                                v-if="latestReferral.fromUser"
                                                :rating="peerRating"
                                                :enableViewDetails="true"
                                                @viewDetailsClicked="viewPeerReviewDetailsClicked"
                                            ></common-rating-stars>
                                            <ratings-summary-modal
                                                v-if="showPeerReviewModal"
                                                :ratings="ratingsForPeerModal"
                                                :summaryText="summaryTextForPeerReviewModal"
                                                :summarySubText="summarySubTextForPeerReviewModal"
                                                :maxGrade="5"
                                                :highlightedRatingKey="'overallSatisfaction'"
                                                :numberedList="false"
                                                @close="viewPeerReviewDetailsModalClosed"
                                            ></ratings-summary-modal>
                                        </template>
                                    </div>
                                </div>
                                <!-- step 6 -->
                                <div v-if="type === ReferralPathTypeEnum.MADE" :class="step6Class">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="peerRatingStatus === 'done'" class="check">✓</span>
                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p>{{ patientRatingText }}</p>

                                            <span v-if="isReferralCompleted(latestReferral) && latestReferral.patientReview">
                                                {{ patientRatingDate }}
                                            </span>
                                        </div>
                                    </div>

                                    <div class="right-content">
                                        <template v-if="isReferralCompleted(latestReferral) && latestReferral.patientReview">
                                            <common-rating-stars
                                                :rating="patientRating"
                                                :enableViewDetails="true"
                                                @viewDetailsClicked="viewPatientReviewDetailsClicked"
                                            ></common-rating-stars>

                                            <ratings-summary-modal
                                                v-if="showPatientReviewModal"
                                                :ratings="ratingsForPatientModal"
                                                :summaryText="summaryTextForPatientReviewModal"
                                                :maxGrade="5"
                                                :highlightedRatingKey="'overallSatisfaction'"
                                                :numberedList="false"
                                                @close="viewPatientReviewDetailsModalClosed"
                                            ></ratings-summary-modal>
                                        </template>
                                    </div>
                                </div>
                                <!-- step 7 -->
                                <div v-if="type === ReferralPathTypeEnum.MADE" :class="step7Class">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="step7Status === 'done'" class="check">✓</span>
                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p>{{ physicianRatingText }}</p>

                                            <span
                                                v-if="
                                                    isReferralCompleted(latestReferral) && latestReferral.physicianReview?.ratings
                                                "
                                            >
                                                {{ peerRatingDate }}
                                            </span>
                                        </div>
                                    </div>

                                    <div class="right-content">
                                        <template
                                            v-if="isReferralCompleted(latestReferral) && latestReferral.physicianReview?.ratings"
                                        >
                                            <common-rating-stars
                                                :rating="peerRating"
                                                :enableViewDetails="true"
                                                @viewDetailsClicked="viewPeerReviewDetailsClicked"
                                            ></common-rating-stars>
                                            <ratings-summary-modal
                                                v-if="showPeerReviewModal"
                                                :ratings="ratingsForPeerModal"
                                                :summaryText="summaryTextForPhysicianReviewModal"
                                                :maxGrade="5"
                                                :highlightedRatingKey="'overallSatisfaction'"
                                                :numberedList="false"
                                                @close="viewPeerReviewDetailsModalClosed"
                                            ></ratings-summary-modal>
                                        </template>

                                        <template v-else-if="showPendingReviewBlock">
                                            <common-button
                                                :label="$t('pages.referrals.show.buttons.rate_referral')"
                                                type="primary"
                                                size="tiny"
                                                @click="rateReferralClicked"
                                            ></common-button>
                                        </template>

                                        <template v-else>
                                            <span></span>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="referral-tracking-modal__activity">
                            <span>{{ $t('pages.referrals.index.referral_modal.last_acti') }}</span>
                            <span class="bold">5 hours ago</span>
                        </div>
                        <p class="referral-tracking-modal__month-date">
                            {{ $t('pages.referrals.index.referral_modal.needed') }}
                        </p>
                        <div class="referral-tracking-modal__needed">
                            <p>{{ getReferralNextAction(latestReferral) }}</p>
                        </div>
                        <div v-if="referral.referralNotes && referral.referralNotes.length">
                            <p class="referral-tracking-modal__month-date">
                                {{ $t('common.referral_details.notes') }}
                            </p>
                            <common-summary-item :isInline="false" :isCompact="false" width="full">
                                <common-images
                                    ref="images"
                                    :images="referral.referralNotes"
                                    :showMoreOption="true"
                                ></common-images>
                            </common-summary-item>
                        </div>
                        <div v-if="additionalRecordRequests.length" class="referral-tracking-modal__additional-records">
                            <h3 class="section-heading">
                                {{ $t('pages.referrals.new.dropdown.label.referred.additional_records') }}
                            </h3>

                            <div class="request-list">
                                <div v-for="request in additionalRecordRequests" :key="request.id" class="request-row">
                                    <div class="request-row__header">
                                        <div>
                                            <div class="request-title">
                                                {{ $t('modals.referral_detail_modal.created_on') }}
                                                {{ dateFormate(request.createdDateTime) }}
                                            </div>
                                        </div>
                                    </div>

                                    <div v-if="request.overallNote" class="request-message">
                                        <strong>{{ $t('modals.referral_detail_modal.note') }}</strong>
                                        {{ request.overallNote }}
                                    </div>

                                    <div class="request-documents">
                                        <div v-for="doc in request.documents" :key="doc.id" class="document-row">
                                            <div class="document-info">
                                                <common-icon name="file-text" size="medium"></common-icon>

                                                <div>
                                                    <div class="document-name">
                                                        {{ doc.fileName }}
                                                    </div>

                                                    <div class="document-meta">
                                                        {{ formatFileSize(doc.size) }}
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="document-actions">
                                                <common-icon
                                                    name="view"
                                                    size="small"
                                                    @click="viewDocument(doc.url)"
                                                ></common-icon>

                                                <common-icon
                                                    name="download"
                                                    size="small"
                                                    @click="downloadDocument(doc)"
                                                ></common-icon>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </common-content-spacer>
        </common-modal>
        <referrals-patient-details-modal
            v-if="showPatientDetailsModal"
            :referral="latestReferral"
            @close="showPatientDetailsModal = false"
        ></referrals-patient-details-modal>
        <pending-insurance-authorization-modal
            v-if="showInsuranceModal"
            :referral="latestReferral"
            @close="closeInsuranceAuthorizationModal()"
            @close-and-show-info="
                closeInsuranceAuthorizationModal()
                openInsuranceAuthorizationInfoModal()
            "
            @declined="insuranceAuthDenied"
            @obtained="insuranceAuthObtained"
        ></pending-insurance-authorization-modal>
        <referrals-is-patient-seen-modal
            v-if="showPatientSeenModal"
            :referral="latestReferral"
            @patient-seen="patientSeen"
            @skip-all="showPatientSeenModal = false"
            @close="showPatientSeenModal = false"
        ></referrals-is-patient-seen-modal>
        <request-report-modal
            :isOpen="showRequestReportModal"
            :referral="latestReferral"
            @close="closeRequestReportModal"
            @sent="requestReportSent"
        ></request-report-modal>
        <is-report-uploaded-modal
            :isOpen="isReportUploadedModal"
            :referral="latestReferral"
            @refresh-details="refreshReferralDetails"
            @close="isReportUploadedModal = false"
        ></is-report-uploaded-modal>
    </div>
</template>

<script>
    import StagesEnum from '@/enums/StagesEnum'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import ReferralCloseReasonEnum from '@/enums/ReferralCloseReasonEnum'
    import ReferralsPatientDetailsModal from 'components/page-elements/referrals/ReferralsPatientDetailsModal'
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import ReferralConfirmMixin from 'mixins/ReferralConfirmMixin'
    import PendingInsuranceAuthorizationModal from 'components/page-elements/referrals-show-pending/PendingInsuranceAuthorizationModal'
    import ReferralsIsPatientSeenModal from 'components/page-elements/ReferralsIsPatientSeenModal'
    import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import RequestReportModal from 'components/page-elements/referrals/RequestConsultationReportModal'
    import dayjs from 'dayjs'
    import ReferralPathTypeEnum from '@/enums/ReferralPathTypeEnum'
    import ReferralTrackingTypeEnum from '@/enums/ReferralTrackingTypeEnum'
    import IsReportUploadedModal from './IsReportUploadedModal.vue'
    import ReferralPoolProposeNavigationMixin from 'mixins/ReferralPoolProposeNavigationMixin'
    import PatientAPI from 'api/PatientAPI'
    import TimeWindowSearchCriteriaEnum from '@/enums/TimeWindowSearchCriteriaEnum'
    import NextActionsEnum from '@/enums/NextActionsEnum'
    import { dateFormate, isMaskedDateOfBirth } from '~/helpers'
    import FileMixin from 'mixins/FileMixin'
    import TelehealthTypeEnum from '@/enums/TelehealthTypeEnum'
    import DateTimeMixin from 'mixins/DateTimeMixin'

    export default {
        mixins: [
            ReferralStatusMixin,
            ReferralConfirmMixin,
            ReferralStatusMixin,
            ReferralPoolProposeNavigationMixin,
            FileMixin,
            DateTimeMixin,
        ],

        components: {
            ReferralsPatientDetailsModal,
            PendingInsuranceAuthorizationModal,
            ReferralsIsPatientSeenModal,
            RequestReportModal,
            IsReportUploadedModal,
        },

        props: {
            isOpen: { type: Boolean, required: true },
            referral: { type: Object, required: true },
            sourceType: { type: String, required: true },
            type: { type: String, required: true },
            isFetching: { type: Boolean, required: true },
            showAsInnerModal: { type: Boolean, default: false },
        },

        data() {
            return {
                ReferralPathTypeEnum,
                ReferralTrackingTypeEnum,
                ReferralStatusEnum,
                showPatientDetailsModal: false,
                isJustCancelled: false,
                showReferralDetails: false,
                showInsuranceModal: false,
                showPatientSeenModal: false,
                tempReferral: null,
                showRequestReportModal: false,
                reportNewRequestDateTime: null,
                showPatientReviewModal: false,
                showPeerReviewModal: false,
                isProcessing: false,
                isLoading: false,
                isReportUploadedModal: false,
                showRecords: false,
                documentOptions: [],
                selectedDocumentIds: [],
                isFetchingDocuments: false,
                message: '',
                isSendingRecordRequest: false,
            }
        },

        computed: {
            additionalRecordRequests() {
                return (this.latestReferral?.additionalRecordsRequests || [])
                    .filter((request) => request.status === 'FULFILLED')
                    .map((request) => ({
                        ...request,
                        overallNote: request.documents?.[0]?.note || null,
                    }))
            },

            referralRoute() {
                return this.latestReferral.consultationReports && this.latestReferral.consultationReports.length > 0
                    ? {
                          name: `referrals.${this.type}.report`,
                          params: { id: this.latestReferral.id },
                      }
                    : {
                          name: 'patients.all-details.patients',
                          params: { id: this.latestReferral.patient.id },
                      }
            },
            isConsultationNotesPendingReceived() {
                return (
                    this.latestReferral.status === ReferralStatusEnum.CONSULTATION_NOTES_PENDING &&
                    this.type === ReferralPathTypeEnum.RECEIVED
                )
            },

            isConsultationNotesPendingMade() {
                return (
                    this.latestReferral.status === ReferralStatusEnum.CONSULTATION_NOTES_PENDING &&
                    this.type === ReferralPathTypeEnum.MADE
                )
            },
            patientRatingText() {
                return this.showReceivedPatientRating
                    ? this.$t('pages.referrals.show.view_details.rating_for_received', {
                          name: this.toFullName,
                      })
                    : this.$t('pages.referrals.show.view_details.rating_for_new')
            },

            physicianRatingText() {
                return this.showReceivedPhysicianRating
                    ? this.$t('pages.referrals.show.view_details.my_rating_for_received', {
                          name: this.toFullName,
                      })
                    : this.$t('pages.referrals.show.view_details.my_rating_for_new')
            },
            isReceivedReferral() {
                return this.type === ReferralPathTypeEnum.RECEIVED && !this.latestReferral?.isFromPatient
            },

            isMadeDirectReferral() {
                return this.type === ReferralPathTypeEnum.MADE && this.sourceType === ReferralTrackingTypeEnum.DIRECT
            },

            providerSectionType() {
                if (this.isReceivedReferral) return ReferralPathTypeEnum.RECEIVED
                if (this.isMadeDirectReferral) return ReferralPathTypeEnum.MADE
                return null
            },

            providerUser() {
                if (this.providerSectionType === ReferralPathTypeEnum.RECEIVED) {
                    return this.latestReferral?.fromUser || this.latestReferral?.fromPractice
                }
                if (this.providerSectionType === ReferralPathTypeEnum.MADE) {
                    return this.latestReferral?.toUser || this.latestReferral?.practice
                }
                return null
            },

            sectionTitle() {
                if (this.providerSectionType === ReferralPathTypeEnum.RECEIVED) {
                    return this.$t('pages.referrals.show.reschedule_referral_new_modal.roles.referring')
                }
                if (this.providerSectionType === ReferralPathTypeEnum.MADE) {
                    return this.$t('pages.referrals.show.reschedule_referral_new_modal.roles.referred')
                }
                return ''
            },

            specialityUser() {
                if (this.providerSectionType === ReferralPathTypeEnum.RECEIVED) {
                    return this.latestReferral?.fromUser
                }
                if (this.providerSectionType === ReferralPathTypeEnum.MADE) {
                    return this.latestReferral?.toUser
                }
                return null
            },

            referralTypeClass() {
                return (sourceType) => ({
                    'referral-tracking-modal__referrals': true,
                    blue: sourceType === ReferralTrackingTypeEnum.DIRECT,
                    purple: sourceType === ReferralTrackingTypeEnum.MATCH,
                })
            },
            step2Class() {
                return {
                    item: true,
                    done: this.isApproved,
                }
            },
            step4Class() {
                return {
                    item: true,
                    done: this.patientSeenStatus === 'done',
                    crossed: this.patientSeenStatus === 'error',
                }
            },
            step5Class() {
                return {
                    item: true,
                    done: this.referralStatus === 'done',
                    pending: this.referralStatus === 'pending',
                }
            },
            step6Class() {
                return {
                    item: true,
                    done: this.peerRatingStatus === 'done',
                    pending: this.peerRatingStatus === 'pending',
                }
            },
            step7Class() {
                return {
                    item: true,
                    done: this.step7Status === 'done',
                    pending: this.step7Status === 'pending',
                }
            },
            isToUserSubscriptionNetwork() {
                return this.$auth.isPracticeFreePlan()
            },
            createdAt() {
                const date = this.latestReferral.createdDateTime || this.latestReferral.createdAt
                return date ? dayjs(date).format('MMMM D, YYYY') : ''
            },
            fullDoctorName() {
                const user = this.latestReferral.fromUser
                if (!user) return ''
                const name = `${user.firstName || ''} ${user.lastName || ''}`.trim()
                return user.designation ? `${name}, ${user.designation}` : name
            },
            isApproved() {
                const date = this.latestReferral.approveDateTime
                return !!date && dayjs(date).isValid()
            },
            approvedAt() {
                const date = this.latestReferral.approveDateTime
                return date ? dayjs(date).format('MMMM D, YYYY') : ''
            },
            notApprovedText() {
                if (this.latestReferral.status === ReferralStatusEnum.DECLINED) {
                    return this.$t(`pages.referrals.tracking.status_title.DECLINED`)
                } else if (this.latestReferral.closeReason === ReferralCloseReasonEnum.UNANSWERED) {
                    return this.$t('pages.referrals.show.details.cancelled_by_system')
                } else {
                    return this.$t(`pages.referrals.tracking.status_title.CANCELLED`)
                }
            },
            scheduledForDate() {
                const date = this.latestReferral.appointmentDateTime
                if (!date) return ''
                return new Date(date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                })
            },

            scheduledForTime() {
                const date = this.latestReferral.appointmentDateTime
                return date ? dayjs(date).format('hh:mm a') : ''
            },

            isInsuranceAuthObtained() {
                let flag
                if (!this.latestReferral.isInsuranceAuthorizationRequired) {
                    flag = true
                } else if (this.latestReferral.status === ReferralStatusEnum.INSURANCE_AUTH_PENDING) {
                    flag = false
                } else {
                    flag = dayjs(this.latestReferral.insuranceAuthorizationObtainedDateTime).isValid()
                }
                return flag
            },

            insuranceAuthObtainedAt() {
                if (this.latestReferral.isInsuranceAuthorizationRequired) {
                    const d = dayjs(this.latestReferral.insuranceAuthorizationObtainedDateTime)
                    return d.isValid() ? d.format('MMMM D, YYYY') : ''
                } else {
                    return this.createdAt
                }
            },
            isInsuranceAuthDeclined() {
                return (
                    this.latestReferral.status === ReferralStatusEnum.CANCELLED &&
                    this.latestReferral.closeReason === ReferralCloseReasonEnum.INSURANCE_AUTH_DENIED
                )
            },
            insuranceStatus() {
                if (!this.isApproved) return 'initial'
                if (this.isInsuranceAuthObtained) return 'done'
                if (this.isInsuranceAuthDeclined) return 'declined'
                return 'pending'
            },

            insuranceTimelineText() {
                const map = {
                    initial: 'pages.referrals.show.details.insurance_authorization_status',
                    done: 'pages.referrals.show.details.insurance_authorization_obtained',
                    declined: 'pages.referrals.show.details.insurance_authorization_declined',
                    pending: 'pages.referrals.show.details.insurance_authorization_pending',
                }

                return this.$t(map[this.insuranceStatus] || map.initial)
            },
            showInsuranceUpdateButton() {
                return (
                    this.latestReferral.state !== 'CLOSED' &&
                    !this.isAppointmentInPast(this.latestReferral) &&
                    this.latestReferral.status !== 'INACTIVE' &&
                    !['done', 'declined'].includes(this.insuranceStatus)
                )
            },
            isReferralTerminatedAfterApproval() {
                return (
                    this.isApproved &&
                    (this.latestReferral.status === ReferralStatusEnum.NO_SHOW ||
                        (this.latestReferral.status === ReferralStatusEnum.CANCELLED &&
                            [
                                ReferralCloseReasonEnum.REFERRED_CANCELLED,
                                ReferralCloseReasonEnum.REFERRING_CANCELLED,
                                ReferralCloseReasonEnum.UNANSWERED,
                            ].includes(this.latestReferral.closeReason)))
                )
            },
            cancelOrNoShowText() {
                if (this.latestReferral.status === ReferralStatusEnum.NO_SHOW) {
                    return this.$t('pages.referrals.show.details.no_show')
                }
                const reason = this.latestReferral.closeReason
                if (reason === ReferralCloseReasonEnum.UNANSWERED) {
                    return this.$t('pages.referrals.show.details.cancelled_by_system')
                }

                let name
                if (reason === ReferralCloseReasonEnum.REFERRED_CANCELLED) {
                    name = this.toFullName
                } else {
                    name = this.fullDoctorName
                }
                return this.$t('pages.referrals.show.details.appointment_cancelled', { name })
            },
            isReferringPhysician() {
                return this.type === ReferralPathTypeEnum.MADE
            },
            showUpdateStatus() {
                return (
                    (this.latestReferral.status === 'PATIENT_VISIT_PENDING' ||
                        this.latestReferral.status === 'INSURANCE_AUTH_PENDING') &&
                    this.isAppointmentInPast(this.latestReferral) &&
                    !this.isReferringPhysician
                )
            },
            latestReferral() {
                return this.referral || this.tempReferral
            },

            step4Text() {
                if (this.latestReferral.status === ReferralStatusEnum.CANCELLED) {
                    return this.latestReferral?.closeComment || ''
                }

                return this.scheduledForDate
            },

            patientSeenStatus() {
                if (this.isReferralTerminatedAfterApproval) return 'error'
                if (this.latestReferral.status === 'CONSULTATION_NOTES_PENDING' || this.isReferralCompleted(this.latestReferral))
                    return 'done'
                return 'pending'
            },

            isPatientRescheduleRequested() {
                return this.latestReferral?.status === ReferralStatusEnum.RESCHEDULE_REQUESTED_BY_PATIENT
            },

            patientSeenTitle() {
                if (this.isPatientRescheduleRequested) {
                    return this.$t('pages.referrals.show.details.reschedule_requested')
                }
                if (this.patientSeenStatus === 'error') return this.cancelOrNoShowText
                if (this.patientSeenStatus === 'done') {
                    return this.$t('pages.referrals.show.details.patient_seen')
                }
                return this.$t('pages.referrals.show.details.patient_visit')
            },

            referralStatus() {
                if (this.latestReferral.status === ReferralStatusEnum.COMPLETED) return 'done'
                if (this.latestReferral.status === ReferralStatusEnum.CONSULTATION_NOTES_PENDING) return 'pending'
                return ''
            },

            referralStatusTitle() {
                if (this.latestReferral.status === ReferralStatusEnum.COMPLETED)
                    return this.$t('pages.referrals.show.view_details.notes_uploaded')
                if (
                    this.latestReferral.status === ReferralStatusEnum.CONSULTATION_NOTES_PENDING &&
                    this.type === ReferralPathTypeEnum.RECEIVED
                )
                    return this.$t('pages.referrals.show.view_details.pending_consultation_reports')
                if (
                    this.latestReferral.status === ReferralStatusEnum.CONSULTATION_NOTES_PENDING &&
                    this.type === ReferralPathTypeEnum.MADE
                )
                    return this.$t('pages.referrals.show.view_details.consultation_report_pending')

                return this.$t('pages.referrals.show.view_details.consultation_notes')
            },

            notesUploadedDate() {
                return dayjs(this.latestReferral.consultationNoteDateTime).format('MMMM D, YYYY')
            },

            referralReportRequestedDateTime() {
                const localDateTime = this.getLocalDateTime(
                    this.reportNewRequestDateTime || this.latestReferral.notesRequestDateTime,
                )
                return dayjs(localDateTime).format('MMMM D, YYYY')
            },

            peerRatingDate() {
                return dayjs(this.latestReferral.physicianReview.createdDateTime).format('MMMM D, YYYY')
            },

            peerRating() {
                if (this.latestReferral.physicianReview?.ratings) {
                    let keys = this.setKeysForPhysicianRating()
                    return this.calculateRating(this.latestReferral.physicianReview?.ratings, keys)
                }

                return null
            },
            peerRatingStatus() {
                if (
                    this.isReferralCompleted(this.latestReferral) &&
                    (this.latestReferral.physicianReview?.ratings || this.latestReferral.patientReview)
                ) {
                    return 'done'
                }
                return 'pending'
            },
            step7Status() {
                if (this.isReferralCompleted(this.latestReferral) && this.latestReferral.physicianReview?.ratings) {
                    return 'done'
                }
                return 'pending'
            },
            proposeSlotsCondition() {
                return (
                    this.type === ReferralPathTypeEnum.RECEIVED &&
                    this.sourceType === ReferralTrackingTypeEnum.MATCH &&
                    [ReferralStatusEnum.CREATED, ReferralStatusEnum.SCHEDULED, ReferralStatusEnum.INACTIVE].includes(
                        this.latestReferral?.status,
                    )
                )
            },
            acceptSlotsConditon() {
                return (
                    this.type === ReferralPathTypeEnum.MADE &&
                    this.sourceType === ReferralTrackingTypeEnum.MATCH &&
                    [ReferralStatusEnum.TIME_SLOTS_PROPOSED, ReferralStatusEnum.SCHEDULED, ReferralStatusEnum.INACTIVE].includes(
                        this.latestReferral?.status,
                    )
                )
            },
            showTimelineCondition() {
                return (
                    this.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                    [ReferralPathTypeEnum.RECEIVED, ReferralPathTypeEnum.MADE].includes(this.type)
                )
            },
            toFullName() {
                if (this.latestReferral.practice?.id) {
                    return this.latestReferral.practice.name
                } else {
                    const user = this.latestReferral.toUser
                    if (!user) return ''
                    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim()
                    return user.designation ? `${name}, ${user.designation}` : name
                }
            },
            referralStatusClass() {
                if (this.latestReferral.status === 'SCHEDULED') return 'done'
                if (this.latestReferral.status === 'INACTIVE') return 'crossed'
                return ''
            },
            shouldShowRightContent() {
                return this.shouldShowReferralDetails(this.latestReferral) && !this.$auth.isUserPracticeOther() && this.isApproved
            },
            showPatientSeenPending() {
                return (
                    this.patientSeenStatus === 'pending' &&
                    this.isAppointmentInPast(this.latestReferral) &&
                    !this.isReferringPhysician
                )
            },
            showReceivedPatientRating() {
                return this.isReferralCompleted(this.latestReferral) && this.latestReferral.patientReview
            },

            showReceivedPhysicianRating() {
                return this.isReferralCompleted(this.latestReferral) && this.latestReferral.physicianReview?.ratings
            },

            showPendingReviewBlock() {
                return (
                    this.isReferralCompleted(this.latestReferral) &&
                    !this.latestReferral.physicianReview?.ratings &&
                    this.latestReferral.patientReview
                )
            },

            isStatusCompletedAndUserNotOther() {
                return this.isReferralCompleted(this.latestReferral) && !this.$auth.isUserPracticeOther()
            },
            uploadRecordsCondition() {
                return (
                    this.type === ReferralPathTypeEnum.MADE &&
                    this.sourceType === ReferralTrackingTypeEnum.MATCH &&
                    [ReferralStatusEnum.CREATED].includes(this.latestReferral?.status) &&
                    this.hasPendingAdditionalRecordsRequest(this.latestReferral)
                )
            },
            uploadRecordsStatus() {
                return this.isUploadRecordsCompleted ? 'done' : 'current'
            },
            isUploadRecordsCompleted() {
                const providers = this.pendingAdditionalRecordProviders

                return providers.length && providers.every((provider) => provider.additionalRecordsRequestStatus === 'FULFILLED')
            },
            uploadRecordsTitle() {
                if (this.isUploadRecordsCompleted) {
                    return this.$t('pages.referrals.show.view_details.records_uploaded')
                }

                return this.pendingAdditionalRecordProviders.length > 1
                    ? this.$t('pages.referrals.show.view_details.records_required')
                    : this.$t('pages.referrals.show.view_details.records_required_single')
            },
            pendingAdditionalRecordProviders() {
                return this.latestReferral.stageInfo.totalProviders.filter((provider) => provider.requestedAdditionalRecords)
            },
        },

        methods: {
            dateFormate,
            getStep1Label() {
                return this.type === ReferralPathTypeEnum.MADE && this.sourceType === ReferralTrackingTypeEnum.MATCH
                    ? this.$t('pages.referrals.show.details.referral_made')
                    : this.$t('pages.referrals.show.details.referral_received')
            },
            getTimeframeLabel(referral) {
                const timeframe = referral?.timeframe

                const label = TimeWindowSearchCriteriaEnum.find((item) => item.value === timeframe)?.label

                return label || referral?.customDate || timeframe
            },
            getUserFullName(patient) {
                return patient ? `${patient.firstName} ${patient.lastName}` : 'N/A'
            },
            getPatientDateOfBirth(dob) {
                if (isMaskedDateOfBirth(dob)) return '—'

                const [year, month, day] = dob.split('-')
                return `${month}-${day}-${year}`
            },
            getTelehealthPreference(telehealthType) {
                if (telehealthType === undefined || telehealthType === null) return ''
                const normalizedTelehealthType = telehealthType === false ? TelehealthTypeEnum.IN_PERSON_ONLY : telehealthType
                const type = Object.keys(TelehealthTypeEnum).find((key) => TelehealthTypeEnum[key] === normalizedTelehealthType)

                return type ? this.$t(`forms.referrals.search.fields.telehealth.options.${type}`) : ''
            },
            getUserName(referral) {
                if (!referral) return 'N/A'

                const isMade = this.type === ReferralPathTypeEnum.MADE

                const user = isMade ? referral?.toUser : referral?.fromUser
                const practice = isMade
                    ? referral?.practice || referral?.toPractice || referral?.fromPractice
                    : referral?.fromPractice || referral?.fromUser

                if (user && (user.firstName || user.lastName)) {
                    const firstName = user.firstName || ''
                    const lastName = user.lastName || ''
                    const fullName = `${firstName} ${lastName}`.trim()
                    if (fullName && user.designation) {
                        return `${fullName}, ${user.designation}`
                    }
                    if (fullName) return fullName
                }

                if (practice?.name) return practice.name

                if (practice?.firstName || practice?.lastName) {
                    const firstName = practice.firstName || ''
                    const lastName = practice.lastName || ''
                    const fullName = `${firstName} ${lastName}`.trim()
                    if (fullName && practice.designation) {
                        return `${fullName}, ${practice.designation}`
                    }
                    if (fullName) return fullName
                }

                return 'N/A'
            },
            getUserSpeciality(user, referral = null) {
                if (user?.specialities && user.specialities.length > 0) {
                    return user.specialities[0]?.speciality?.name || ''
                }

                const ref = referral || this.latestReferral

                if (ref?.speciality?.name) {
                    return ref.speciality.name
                }

                if (ref?.subSpeciality?.name) {
                    return ref.subSpeciality.name
                }

                if (typeof ref?.speciality === 'string') {
                    return ref.speciality
                }

                if (ref?.facilityServiceTests && ref.facilityServiceTests.length > 0) {
                    return ref.facilityServiceTests[0]?.name || ''
                }

                return ''
            },
            getReferralType(type) {
                if (type === ReferralTrackingTypeEnum.DIRECT) return this.$t('pages.referrals.index.referrals_made.type_direct')
                if (type === ReferralTrackingTypeEnum.MATCH) return this.$t('pages.referrals.index.referrals_made.type_matched')
                return 'N/A'
            },
            getReferralNextAction(referral) {
                if (this.hasPendingAdditionalRecordsRequest(referral)) {
                    return NextActionsEnum.UPLOAD_RECORDS
                }

                return referral?.stageInfo?.nextAction
            },
            getStageClass(referral) {
                if (this.hasPendingAdditionalRecordsRequest(referral)) {
                    return 'referral-tracking__referrals diagnosis'
                }
                const stage = referral?.stageInfo?.stage
                if (!stage) return 'referral-tracking__referrals gray'

                switch (stage) {
                    case StagesEnum.CONFIRMED:
                    case StagesEnum.AWAITING_PATIENT_VISIT:
                        return 'referral-tracking__referrals green'

                    case StagesEnum.REFERRAL_SUBMITTED:
                    case StagesEnum.AWAITING_PROVIDER_PROPOSALS:
                    case StagesEnum.AWAITING_REFERRER_SELECTION:
                    case StagesEnum.PENDING_PROVIDER_CONFIRMATION:
                    case StagesEnum.AWAITING_PROPOSALS:
                        return 'referral-tracking__referrals yellow'

                    case StagesEnum.PROPOSALS_CLOSED:
                        return 'referral-tracking__referrals purple'

                    case StagesEnum.SELECT_TIME_SLOT:
                    case StagesEnum.PROPOSE_TIME_SLOTS:
                        return 'referral-tracking__referrals blue'

                    case StagesEnum.CANCELLED_NO_PROPOSALS:
                    case StagesEnum.CANCELLED_NO_SELECTION:
                        return 'referral-tracking__referrals today'

                    default:
                        return 'referral-tracking__referrals gray'
                }
            },
            formatStage(referral) {
                let stage = referral?.stageInfo?.stage

                if (this.hasPendingAdditionalRecordsRequest(referral)) {
                    stage = StagesEnum.UPLOAD_RECORDS
                }
                if (!stage) return 'N/A'

                return stage
                    .toLowerCase()
                    .split('_')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')
            },
            hasPendingAdditionalRecordsRequest(referral) {
                if (
                    this.type !== ReferralPathTypeEnum.MADE ||
                    this.sourceType !== ReferralTrackingTypeEnum.MATCH ||
                    referral?.status !== ReferralStatusEnum.CREATED
                ) {
                    return false
                }

                const providers = referral?.stageInfo?.totalProviders?.length
                    ? referral.stageInfo.totalProviders
                    : referral?.stageInfo?.providersWithoutSlots || []

                return providers.some(
                    (provider) => provider.requestedAdditionalRecords && provider.additionalRecordsRequestStatus === 'PENDING',
                )
            },
            getCreatedDate(latestReferral) {
                const dateValue = latestReferral?.createdDateTime || latestReferral?.createdAt

                if (!dateValue) return ''

                return new Date(dateValue).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                })
            },
            getCreatedDateTime(latestReferral) {
                const date = new Date(latestReferral.createdDateTime)

                const formattedDate = date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                })

                const formattedTime = date.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                })

                return `${formattedDate} at ${formattedTime}`
            },
            getAppointmentDateTime(latestReferral) {
                const date = new Date(latestReferral.appointmentDateTime)

                const formattedDate = date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                })

                const formattedTime = date.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                })

                return `${formattedDate} at ${formattedTime}`
            },
            showProviderSection() {
                const isPatientReferralRoute = this.$route.path.includes('/referrals-patient')
                if (isPatientReferralRoute) {
                    return false
                }
                return (
                    this.type === ReferralPathTypeEnum.RECEIVED ||
                    (this.type === ReferralPathTypeEnum.MADE && this.sourceType === ReferralTrackingTypeEnum.DIRECT)
                )
            },
            openReferralInfoModal() {
                this.showPatientDetailsModal = true
            },
            openInsuranceAuthorizationModal() {
                this.showInsuranceModal = true
            },
            closeInsuranceAuthorizationModal() {
                this.showInsuranceModal = false
            },
            openPatientSeenModal() {
                this.showPatientSeenModal = true
            },
            patientSeen(event) {
                if (this.$auth.isUserPracticeDemo()) {
                    return
                }
                ReferralsAPI.updatePatientVisitStatus(this.latestReferral.id, event.status).then((ev) => {
                    this.showPatientSeenModal = false
                    this.latestReferral.status = ev.status
                    this.latestReferral.insuranceAuthorizationObtainedDateTime = ev.insuranceAuthorizationObtainedDateTime
                    if (event.status === 'patient-seen') {
                        this.$router.push({
                            name: `referrals.received.add-report`,
                            params: {
                                id: this.latestReferral.id,
                            },
                        })
                    }
                })
            },
            reportAlreadyAdded() {
                this.isLoading = true
                ReferralsAPI.sendConsultationReport(this.latestReferral.id, true).then(() => {
                    this.refreshReferralDetails()
                })
            },
            addNotesClicked() {
                this.$router.push({
                    name: `referrals.received.add-report`,
                    params: {
                        id: this.latestReferral.id,
                    },
                })
            },

            openRequestReportModal() {
                this.showRequestReportModal = true
            },

            closeRequestReportModal() {
                this.showRequestReportModal = false
            },

            requestReportSent() {
                this.reportNewRequestDateTime = dayjs().format()
                this.closeRequestReportModal()
                this.emitEvent({ name: 'report-request-sent' })
            },

            viewPeerReviewDetailsClicked() {
                this.showPeerReviewModal = true
            },

            viewPeerReviewDetailsModalClosed() {
                this.showPeerReviewModal = false
            },

            processReferral() {
                const isFromPatient = Boolean(
                    this.latestReferral?.isFromPatient ||
                        this.$route.path.includes('/referrals-patient') ||
                        (!this.latestReferral?.fromUser && this.latestReferral?.patient?.id),
                )
                this.goToProposeTime({ ...this.latestReferral, isFromPatient }, this.type)
                this.$emit('close', true)
            },

            refreshReferralDetails() {
                this.isLoading = true
                const userId = this.$auth.getActiveMember()?.id || this.$auth.getUser().id
                if (this.sourceType === ReferralTrackingTypeEnum.DIRECT) {
                    ReferralsAPI.get(this.latestReferral.id, userId)
                        .then((res) => {
                            this.tempReferral = res
                        })
                        .finally(() => (this.isLoading = false))
                } else {
                    ReferralsPoolAPI.getReferralPoolById(this.latestReferral.id, this.type, this.$auth.getActiveMember()?.id)
                        .then((res) => {
                            this.tempReferral = res
                        })
                        .finally(() => (this.isLoading = false))
                }
            },
            declineReferral() {
                ReferralsPoolAPI.decline(this.latestReferral.id, {
                    referralPoolId: this.latestReferral.id,
                    userId: this.$auth.getActiveMember()?.id || this.$auth.getUser().id,
                    practiceId: this.$auth.getPractice()?.id,
                }).then(() => {
                    this.refreshReferralDetails()
                    this.emitEvent({
                        name: 'fetch-referral',
                        payload: this.latestReferral.id,
                    })
                })
            },
            openRequestAdditionalRecords() {
                this.showRecords = true
                this.getDocumentType()
            },
            getDocumentType() {
                if (this.documentOptions.length) return
                this.isFetchingDocuments = true
                PatientAPI.getDocumentType().then((res) => {
                    this.documentOptions = res.map((item) => ({
                        label: item.name,
                        value: item.id,
                    }))
                    this.isFetchingDocuments = false
                })
            },
            requestAdditionalRecords() {
                if (!this.selectedDocumentIds.length) {
                    return
                }
                this.isSendingRecordRequest = true
                ReferralsPoolAPI.additionalRecordRequest(this.latestReferral.id, {
                    recordTypeIds: this.selectedDocumentIds,
                    message: this.message,
                })
                    .then(async () => {
                        this.isSendingRecordRequest = false
                        this.showRecords = false
                        this.selectedDocumentIds = []
                        this.message = ''
                        this.emitEvent({ name: 'records-requested' })
                    })
                    .catch(() => {
                        this.isSendingRecordRequest = false
                    })
            },
            continueUploadRecords() {
                this.emitEvent({
                    name: 'open-providers-modal',
                    payload: this.latestReferral.id,
                })
            },
            viewDocument(url) {
                const routeData = this.$router.resolve({ name: 'image.preview', query: { url } })
                window.open(routeData.href, '_blank')
            },

            downloadDocument(document) {
                this.downloadFile(document.url, document.fileName)
            },
            formatFileSize(bytes) {
                if (!bytes) return '0 KB'

                const units = ['KB', 'MB', 'GB']
                let size = bytes
                let i = 0

                while (size >= 1024 && i < units.length - 1) {
                    size /= 1024
                    i + 1
                }

                return `${size % 1 === 0 ? size : size.toFixed(1)} ${units[i]}`
            },
        },
        watch: {
            proposeSlotsCondition: {
                immediate: true,
                handler(val) {
                    if (val) {
                        this.getDocumentType()
                    }
                },
            },
        },
        mounted() {
            if (this.$route.params.section === 'records') {
                this.openRequestAdditionalRecords()
            }
        },
    }
</script>
